import type { RiderInfo } from '../model/schema.js';
import { addMinutes, parseClock, parseDay } from './time.js';

/** Length of a schedule entry without an end and without a later entry that day. */
export const DEFAULT_ENTRY_MINUTES = 30;

/** A rider schedule entry that is a calendar entry (U85), in local venue time. */
export interface ShowEntry {
  id: string;
  label: string;
  day: string;
  start: string;
  endDay: string;
  end: string;
}

/**
 * The show schedule of a board as calendar entries (U85): rider schedule entries whose time is a
 * clock time and which have a day (their own or the rider date). The end is the entry's own end,
 * else the next entry's start that day, else start + 30 min; an end before the start is the next day.
 */
export function showEntries(rider: RiderInfo | undefined): ShowEntry[] {
  const fallbackDay = parseDay(rider?.date);
  const timed = (rider?.schedule ?? []).flatMap((e) => {
    const start = parseClock(e.time);
    const day = parseDay(e.date) ?? fallbackDay;
    return start && day ? [{ e, day, start }] : [];
  });
  const sorted = [...timed].sort((a, b) =>
    `${a.day} ${a.start}`.localeCompare(`${b.day} ${b.start}`),
  );
  return timed.map(({ e, day, start }) => {
    const own = parseClock(e.end);
    let endDay = day;
    let end: string;
    if (own) {
      end = own;
      if (own <= start) endDay = addMinutes(day, '00:00', 24 * 60)[0];
    } else {
      const next = sorted.find((n) => n.day === day && n.start > start);
      if (next) end = next.start;
      else [endDay, end] = addMinutes(day, start, DEFAULT_ENTRY_MINUTES);
    }
    return { id: e.id, label: e.label, day, start, endDay, end };
  });
}
